import { useEffect, useRef, forwardRef, useMemo } from 'react';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';
import { sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import TranslationText from '@Components/Utilities/TranslationText';
import axios from 'axios';
import MuiTextField from '@mui/material/TextField';

const TextField = forwardRef((props, ref) => {

    const value = useRef(props.value);
    const fieldID = props.id ? props.id : props.name + 'ID';
    const minLength = 3;
    const validTextFieldTypes = ['text', 'number', 'search'];
    const cancelToken = useRef(null);

    /*
     * Any updates to this file should be tested across multiple locations as they keep breaking.
     * Test the Quick Search, Faceted Quick Search & Mobile Quick Search Autocomplete
     * Want To Buy & Email Alerts Make / Model Autocomplete
     * Distance Sort Modal now uses this as well
     */
    const customAutoSuggestRenderItem = (ul, item) => {
        let keywords = value.current.trim().replace(/\(|\)/g, '').split(' ').join('|');
        let suggestion = item.Label.replace(new RegExp('(' + keywords + ')', 'gi'), '<strong>$1</strong>');
        var liItem = $('<li>')
            .attr('data-value', item.Label)
            .attr('data-url', item.Url)
            .append(suggestion)
            .appendTo(ul);

        if (item?.Indent > 0) {
            var defaultPadding = 10;
            var pLeft = defaultPadding + (item.Indent * 10);
            liItem.css('padding-left', `${pLeft}px`)
        }

        return liItem;
    };

    useEffect(() => {
        if (props.autoComplete) {

            let selectCallback = (event, ui) => {
                value.current = ui.item.value;
                if (props.updateField) {
                    if (ui.item.airportcodeid || ui.item.cityid) {
                        props.updateField(ui.item.value, ui.item.airportcodeid, ui.item.cityid, ui.item.lat, ui.item.lng)
                    }
                    else {
                        props.updateField(ui.item.value);
                    }
                }
            };

            if (props.autoComplete.callback instanceof Function) {
                selectCallback = props.autoComplete.callback;
            }

            let elID = ('#' + fieldID);
            let parentEl = $(elID).parent();
            let newWidth = parentEl.outerWidth();
            if (props.autoSuggestWidth) {
                if (props.autoSuggestWidth instanceof Function) {
                    newWidth = props.autoSuggestWidth();
                } else {
                    newWidth = props.autoSuggestWidth;
                }
            }

            $(elID).autocomplete({
                source: function (request, response) { return []; },
                delay: props.autoCompleteDelay || 500,
                minLength: minLength,
                select: selectCallback,
                appendTo: parentEl,
                open: function (result) {
                    if (navigator.userAgent.match(/(iPod|iPhone|iPad)/)) {
                        $('.ui-autocomplete').off('menufocus hover mouseover');
                    }
                },
                create: function () {
                    $(this).data('ui-autocomplete')._renderItem = function (ul, item) { return customAutoSuggestRenderItem(ul, item); };
                    if (newWidth && newWidth > 0) {
                        $(this).data('ui-autocomplete')._resizeMenu = function () { this.menu.element.outerWidth(newWidth); };
                    }
                },
                position: props.position || { my: 'left+0 top+15' }
            });

            
        }
    }, []);

    const ajaxFetch = useMemo(() =>
        debounce((ajaxOptions) => sandhillsRequest(ajaxOptions).then((data) => {
            handleResponse(data);
        }).catch((error) => {
            if (!axios.isCancel(error)) {
                console.log('Request error', error);
            }
        }), 750), []
    );

    const handleResponse = (data) => {
        var field = $('#' + fieldID);
        field.autocomplete('option', 'source', function (request, response) { response(data); });
        field.autocomplete('search');
    };

    const delayQuery = useMemo(() => throttle(e => autoComplete(e), 2000), [props.autoComplete?.postData]);

    const fieldChange = (e) => {
        value.current = e.target.value;
        props.onFieldChanged(e);
        if (props.autoComplete) {
                delayQuery(e);
        }
    };

    const autoComplete = (e) => {
        let selectCallback = (event, ui) => {
            value.current = ui.item.value;
            if (props.updateField) {
                props.updateField(ui.item.value);
            }
        };

        if (props.autoComplete.callback instanceof Function) {
            selectCallback = props.autoComplete.callback;
        }

        if (props.autoComplete.ajaxUrl && props.autoComplete.ajaxUrl instanceof Function && e.target.value) {
            if (cancelToken.current) {
                cancelToken.current.cancel();
                cancelToken.current = null;
            }

            if (e.target.value.length >= minLength) {
                let postData = {};
                if (props.autoComplete.postData instanceof Function) {
                    postData = props.autoComplete.postData(e);
                }
                const token = axios.CancelToken;
                cancelToken.current = token.source();
                    ajaxFetch({
                        url: props.autoComplete.ajaxUrl(e),
                        data: postData,
                        cancelToken: cancelToken.current.token,
                        errorHandling: {
                            useConsole: false,
                            useAlert: false
                        }
                    });
            } else {
                handleResponse([]);
            }
        } else if (props.autoComplete.suggestions) {
            $('input[name=' + props.name + ']').autocomplete({
                source: props.autoComplete.suggestions,
                select: selectCallback
            });
        }
    };

    const fieldBlur = (e) => {
        if (props.onFieldBlur) {
            props.onFieldBlur(e);
        }
    };

    const fieldKeyPress = (e) => {
        if (props.onKeyPress) {
            props.onKeyPress(e);
        }
    };

    return (
        <div className={props.class}>
            {props.label && !props.useMUI && <label className={props.hideLabel ? 'visually-hidden' : props.labelClass || 'bold'} htmlFor={fieldID}> {props.label} {props.required && !props.showRequired && <span className={props.requiredSpanClass || 'required red'} />} {props.symbolLabel && <span className={props.symbolLabelClass}>{props.symbolLabel}</span>}</label>}
            {validTextFieldTypes.includes(props.type) && (
                props.useMUI ? (
                    <MuiTextField
                        label={props.label}
                        disabled={props.disabled}
                        aria-required={props.required}
                        aria-invalid={props.validationErrors ? true : false}
                        className={props.inputClass}
                        type={props.type}
                        readOnly={props.ReadOnly ? 'readOnly' : null}
                        value={props.value ? props.value : ''}
                        name={props.name}
                        id={fieldID}
                        onChange={fieldChange}
                        onBlur={fieldBlur}
                        autoComplete={props.htmlAutoComplete}
                        onKeyDown={fieldKeyPress}
                        min='0'
                        ref={ref}
                        sx={props.sx}
                        InputProps={props.InputProps}
                        multiline={props.multiline}
                        fullWidth={props.fullWidth}
                    />
                ) : (
                    <>
                        <input
                            disabled={props.disabled}
                            aria-required={props.required}
                            aria-invalid={props.validationErrors ? true : false}
                            className={props.inputClass}
                            type={props.type}
                            readOnly={props.ReadOnly ? 'readOnly' : null}
                            value={props.value ? props.value : ''}
                            placeholder={props.placeholder}
                            name={props.name}
                            id={fieldID}
                            onChange={fieldChange}
                            onBlur={fieldBlur}
                            autoComplete={props.htmlAutoComplete}
                            onKeyDown={fieldKeyPress}
                            min='0'
                            ref={ref}
                        />
                        {props.required && props.showRequired && (
                            <span className={props.requiredSpanClass || 'required red next-line'}>
                                <TranslationText tKey='sandhills.required' />
                            </span>
                        )}
                    </>
                )
            )}
            {props.type === 'textarea' && (
                <textarea
                    className={props.inputClass}
                    readOnly={props.ReadOnly ? 'readOnly' : null}
                    value={props.value ? props.value : ''}
                    placeholder={props.placeholder}
                    name={props.name}
                    id={fieldID}
                    onChange={fieldChange}
                    maxLength='500'
                    ref={ref}
                    disabled={props.disabled}
                />
            )}
            <span className='error'>{props.validationErrors}</span>
        </div>
    );
});

export default TextField;